import { Browser } from "../../core/browser";
import { AuxiliumComponent } from "../auxilium-component";

export default class Input extends AuxiliumComponent {
  static observable = ["value", "placeholder", "icon-style", "type", "suffix-button", "suffix-tooltip", "readonly", "variation", "helper-text", "required", "max-length", "label", "description", "secondary-label", "suffix-icon", "prefix-icon", "orientation", "label-tooltip", "casing", "size", "prefix-button", "prefix-tooltip", "prefix-description", "suffix-description", "route", "max-length"];
  static observableBooleans = ["disable-messages", "show-password-button", "readonly"];

  #Mappers = {};

  #GetSearchedItems = null;
  #InputTimeout;
  #Dropdown = null;

  constructor() {
    super();

    this.InitializeValidationMessages();

    this.type ??= "text";
    this.searchParameter ??= "q";

    if (this.type === "password" && this.showPasswordButton) {
      this.suffixButton = "fas fa-eye";
      this.suffixTooltip = Localizer.SHOW_PASSWORD_BUTTON;

      this.addEventListener("suffixclick", () => {
        if (this.type === "password") {
          this.suffixButton = "fas fa-eye-slash";
          this.type = "text";
          this.suffixTooltip = Localizer.HIDE_PASSWORD_BUTTON;
        } else {
          this.suffixButton = "fas fa-eye";
          this.type = "password";
          this.suffixTooltip = Localizer.SHOW_PASSWORD_BUTTON;
        }
      });
    }

    if (this.type === "search" && !this.prefixIcon && !this.prefixButton) {
      this.prefixIcon = "fal fa-search";
    }

    this.value = (() => {
      switch (this.casing?.toLowerCase()) {
        case "uppercase": return this.getAttribute("value")?.toUpperCase();
        case "lowercase": return this.getAttribute("value")?.toLowerCase();
        default: return this.getAttribute("value")
      }
    })() ?? "";

    this.initialValue = this.value;

    this.#Mappers = {
      list: this.getAttribute("list"),
      max: this.getAttribute("max"),
      maxlength: this.maxLength,
      min: this.getAttribute("min"),
      form: this.getAttribute("form"),
      minlength: this.getAttribute("minlength"),
      name: this.getAttribute("name"),
      autofocus: this.hasAttribute("focus"),
      autocomplete: this.getAttribute("autocomplete"),
      pattern: this.getAttribute("pattern"),
      placeholder: this.getAttribute("placeholder"),
      readonly: this.readonly,
      required: this.hasAttribute("required"),
      step: this.getAttribute("step"),
      tabIndex: this.getAttribute("tabindex") ?? ""
    };

    if (this.closest("form") && !this.#Mappers.name) {
      this.#Mappers.name = this.id || this.identifier;
    }

    if (this.type.toLowerCase() === "date" && !this.#Mappers.max) {
      this.#Mappers.max = "9999-12-31";
    }

    Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);
  }

  get GetSearchedItems() {
    return this.#GetSearchedItems;
  }

  set GetSearchedItems(value) {
    this.#GetSearchedItems = value;

    const dropdown = this.querySelector("chip-dropdown");

    if (dropdown) {
      dropdown.GetSearchedItems = value;

      if (this.value) {
        this.#ForceAutocomplete(this.querySelector("input"));
      }
    }
  }

  #OnInput(input, wasDynamic) {
    // Other types will error when setting these values
    if (["text", "search", "url", "tel", "password"].includes(this.type.toLowerCase())) {
      const trueValue = (() => {
        switch (this.casing?.toLowerCase()) {
          case "uppercase": return input.value.toUpperCase();
          case "lowercase": return input.value.toLowerCase();
          default: return input.value
        }
      })() ?? "";

      Object.assign(input, {
        value: trueValue,
        selectionEnd: input.selectionStart
      });

      this.value = trueValue;
    } else {
      this.value = input.value;
    }

    if (this.#Dropdown && !wasDynamic) {
      if (this.#InputTimeout) {
        clearTimeout(this.#InputTimeout);
      }

      this.#InputTimeout = setTimeout(() => {
        this.#ForceAutocomplete(input);
      }, 500);
    }
  }

  #ForceAutocomplete(input) {
    const value = input?.value ?? this.value;

    this.#Dropdown?.Search(value).then(() => {
      if (!this.#Dropdown.querySelector("chip-dropdownitem") || value.trim() === "") {
        this.#Dropdown.Close();
      } else {
        this.#Dropdown.Open();
      }
    });
  }

  parsedCallback() {
    this.removeAttribute("tabindex");

    const element = document.createElementWithContents("div",
      `
        <div class="input__wrapper">
          <input class="input-field"/>
        </div>
      `.trim()
    ),
      input = element.querySelector("input");

    Object.assign(input, {
      type: this.type,
      value: this.value,
      id: this.identifier
    });

    if (this.helperText) {
      const helperTextMethod = Browser.GetFlavour() === "gov" ? "prepend" : "appendChild";

      element[helperTextMethod](document.createElementWithContents("div", this.helperText, { className: "input-field__helper-text" }));
    }

    this.#Dropdown = this.querySelector(":scope>chip-dropdown");

    if (this.#Dropdown) {
      this.#ForceAutocomplete(input);
    }

    const label = document.createElementWithContents("label", this.label, {
      className: "input-field__label",
      htmlFor: this.identifier
    });

    if (this.labelTooltip) {
      const labelIcon = document.createElementWithContents("chip-icon", null, { icon: "fas fa-info-circle" });
      labelIcon.setAttribute("tooltip", this.labelTooltip);

      label.appendChild(labelIcon);
    }

    if (this.secondaryLabel) { label.appendChild(document.createElementWithContents("span", this.secondaryLabel, { className: "input-field__label--secondary" })); }

    if (!this.orientation) {
      element.prepend(label);
    } else {
      element.querySelector(".input__wrapper").prepend(label);
    }

    input.onchange = ev => {
      ev.preventDefault();
      ev.stopPropagation();

      this.reportValidity();
      this.closest("chip-form")?.checkValidity();

      this.raiseEvent("change");
    }

    input.addEventListener("input", () => this.#OnInput(input));

    if (this.type === "search") {
      input.addEventListener("keyup", ev => {
        if (ev.key === "Enter") {
          if (this.route) {
            const url = Object.assign(new URL(Browser.ResolveRelativeURL(this.route)), {
              hash: ""
            });

            if (input.value.trim() === "") {
              url.searchParams.delete(this.searchParameter);
            } else {
              url.searchParams.set(this.searchParameter, input.value.trim());
            }

            location.href = url;
          } else {
            Browser.SetURLParameter(this.searchParameter, input.value.trim() === "" ? null : input.value.trim());
          }
        }
      });
    }

    if (this.#Dropdown) {
      input.addEventListener("keydown", ev => {
        if (ev.key === "ArrowUp") {
          this.#Dropdown.ArrowUp(ev);
        } else if (ev.key === "ArrowDown") {
          this.#Dropdown.ArrowDown(ev);
        }
      });

      if (this.GetSearchedItems) {
        this.#Dropdown.GetSearchedItems = this.GetSearchedItems;
      }

      this.#Dropdown.onselectionchange = () => {
        this.value = this.#Dropdown.value || this.#Dropdown.selectedText;
        this.raiseEvent("dropdownselected", { item: this.#Dropdown.selectedItem });
      }

      input.addEventListener("focus", () => {
        if (input.value.trim() !== "") {
          this.#Dropdown.Open();
        }

        this.raiseEvent("focus");
      });
    }

    if (this.closest("chip-dropdown")) {
      input.onchange = ev => {
        ev.preventDefault();
        ev.halt();
      }
    }

    if (this.suffixIcon) { input.classList.add("border-right-none", "br-right-none"); }
    if (this.type === "date") { input.addEventListener("keydown", this.#HandleDateKeydown); }

    this.#renderFixes(input);

    if (!this.label && !this.secondaryLabel) { element.querySelector("label").remove(); }
    if (!this.label && this.#Mappers.placeholder) { input.ariaLabel = this.#Mappers.placeholder; }

    Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
      .forEach(key => input.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

    if (this.description) { input.ariaLabel = this.description; }

    this.prepend(...element.childNodes);
  }

  #renderFixes(input) {
    if (this.prefixButton) {
      const pfxButtonContent = this.prefixButton.startsWith("fa")
        ? null
        : this.prefixButton;

      const prefixDropdown = this.querySelector("chip-prefixdropdown"),
        pfxButton = document.createElementWithContents(!prefixDropdown ? "chip-button" : "chip-dropdownbutton", pfxButtonContent, {
          className: "input__bound",
          buttonStyle: "icon",
          icon: this.prefixButton.startsWith("fa") ? this.prefixButton : "",
          variation: "body",
          onclick: () => this.raiseEvent("prefixclick")
        });

      if (prefixDropdown) {
        pfxButton.appendChild(prefixDropdown.firstElementChild);
        prefixDropdown.remove();
      }

      input.insertAdjacentElement("beforebegin", pfxButton);
    } else if (this.prefixIcon) {
      input.insertAdjacentElement("beforebegin", document.createElementWithContents("span", document.createElementWithContents("chip-icon", null, { icon: this.prefixIcon }), { className: "input__bound" }));
    }

    if (this.suffixButton) {
      const suffixDropdown = this.querySelector("chip-suffixdropdown"),
        sfxButton = document.createElementWithContents(!suffixDropdown ? "chip-button" : "chip-dropdownbutton", null, {
          className: "input__bound",
          buttonStyle: "icon",
          icon: this.suffixButton,
          variation: "body",
          onclick: () => this.raiseEvent("suffixclick")
        });

      if (suffixDropdown) {
        sfxButton.appendChild(suffixDropdown.firstElementChild);
        suffixDropdown.remove();
      }

      input.insertAdjacentElement("afterend", sfxButton);
    } else if (this.suffixIcon) {
      input.insertAdjacentElement("afterend", document.createElementWithContents("span", document.createElementWithContents("chip-icon", null, { icon: this.suffixIcon }), { className: "input__bound" }));
    }

    if (this.prefixTooltip && input.previousElementSibling) { input.previousElementSibling.tooltip = this.prefixTooltip; }
    if (this.suffixTooltip && input.nextElementSibling) { input.nextElementSibling.tooltip = this.suffixTooltip; }

    if (this.prefixDescription && input.previousElementSibling) { input.previousElementSibling.description = this.prefixDescription; }
    if (this.suffixDescription && input.nextElementSibling) { input.nextElementSibling.description = this.suffixDescription; }
  }

  attributeChanged(name, _, newValue) {
    switch (name) {
      case "placeholder":
      case "type":
        this.querySelector("input")[name] = newValue ?? "";
        break;
      case "max-length":
        this.querySelector("input").maxLength = newValue ?? "";
        break;
      case "required":
        {
          const input = this.querySelector("input");

          if (input) {
            input.required = this.IsAttributeValueTrue(newValue);
          }
        }
        break;
      case "value":
        {
          const input = this.querySelector("input");

          input.value = newValue;
          this.#OnInput(input, true);
        }
        break;
      case "suffix-button":
        const attempt = this.querySelector("input+chip-button.input__bound");

        if (attempt) {
          attempt.icon = newValue;

          if (!newValue) {
            attempt.remove();
          }
        } else {
          this.#renderFixes(this.querySelector("input"));
        }
        break;
      case "suffix-tooltip":
        this.querySelector("input+.input__bound")?.setAttribute("tooltip", newValue);
        break;
      case "readonly":
        this.querySelector("input").readOnly = this.IsAttributeValueTrue(newValue);
        break;
      case "helper-text":
        {
          const existing = this.querySelector(".input-field__helper-text");

          if (existing) {
            if (newValue) {
              existing.textContent = newValue;
            } else {
              existing.remove();
            }
          } else {
            this.appendChild(document.createElementWithContents("div", newValue, { className: "input-field__helper-text" }));
          }
        }
        break;
    }
  }

  checkValidity() {
    const input = this.querySelector("input"),
      isValid = input?.checkValidity() ?? false;

    this.validationMessage = this.GetValidationMessage(input);

    return isValid;
  }

  setInvalid(message) {
    this.querySelector(".input-field__validation-error")?.remove();

    const input = this.querySelector("input"),
      msgElement = document.createElementWithContents("div", message, { className: "input-field__validation-error" });

    if (!this.disableMessages) {
      if (Browser.GetFlavour() === "gov") {
        this.querySelector(".input__wrapper").insertAdjacentElement("beforebegin", msgElement);
      } else {
        this.appendChild(msgElement);
      }
    }

    input.classList.add("input-field__invalid");

    this.querySelectorAll(".input__bound").forEach(x => x.classList.add("input-field__invalid"));
  }

  reportValidity() {
    const validity = this.checkValidity();

    this.querySelector(".input-field__validation-error")?.remove();
    this.querySelectorAll(".input-field__invalid").forEach(x => x.removeClass("input-field__invalid"));

    if (!validity) {
      this.setInvalid(this.validationMessage);
    }

    return validity;
  }

  focus() {
    this.querySelector("input")?.focus();
  }

  blur() {
    this.querySelector("input")?.blur();

    this.querySelectorAll("chip-button.input__bound").forEach(btn => btn.blur());
  }

  select() {
    this.querySelector("input")?.select();
  }

  #HandleDateKeydown(ev) {
    let date;
    const { target } = ev;

    if (ev.key?.toUpperCase() === "T") { date = new Date(); }
    else if (ev.key?.toUpperCase() === "Y") { date = new Date(new Date().setDate(new Date().getDate() - 1)); }

    if (date) {
      try {
        target.valueAsDate = date;
      } catch {
        target.value = date.toISOString().slice(0, 19);
      }

      target.dispatchEvent(new Event("input", {
        view: window,
        bubbles: true,
        cancelable: false
      }));
    }
  }
}
